import { TILE_HEIGHT, TILE_WIDTH } from '@kopi-kaki/protocol';
import { between, fade, shade } from '../pixel.js';
import type { PixelCanvas, Point } from '../pixel.js';

/**
 * Shared isometric drawing helpers for furniture art.
 *
 * Items are composed in "tile space": (u, v) run along the tile's x and y
 * axes with the tile centre at (0, 0) and edges at +/-0.5, and h is height in
 * screen pixels. The canvas origin is already moved to the tile centre, so
 * drawers just call these with tile-space coordinates.
 */

/** Draws one item into a canvas whose origin is the tile centre at floor level. `open` is doors only. */
export type FurniDrawer = (pc: PixelCanvas, rotation: number, open?: boolean) => void;

export const HW = TILE_WIDTH / 2;
export const HH = TILE_HEIGHT / 2;

export function iso(u: number, v: number, h = 0): Point {
  return { x: (u - v) * HW, y: (u + v) * HH - h };
}

export interface Faces {
  top: string;
  left: string;
  right: string;
}

/** Derive lit/shaded faces from one base colour, light coming from the top-left. */
export function faces(base: string, top?: string): Faces {
  return { top: top ?? shade(base, 1.15), left: base, right: shade(base, 0.78) };
}

export interface BoxPiece {
  u0: number;
  u1: number;
  v0: number;
  v1: number;
  h0: number;
  h1: number;
  colors: Faces;
}

export function drawBox(pc: PixelCanvas, b: BoxPiece): void {
  const { u0, u1, v0, v1, h0, h1, colors } = b;
  const tall = h1 - h0;
  // Each face gets a soft wash (lighter up high, darker toward the floor), so
  // solid blocks read as lit volumes rather than flat cut-outs.
  const wash = Math.min(0.1, 0.03 + tall * 0.0018);
  pc.poly([iso(u0, v1, h0), iso(u1, v1, h0), iso(u1, v1, h1), iso(u0, v1, h1)], fade(colors.left, 1 + wash * 0.6, 1 - wash));
  pc.poly([iso(u1, v0, h0), iso(u1, v1, h0), iso(u1, v1, h1), iso(u1, v0, h1)], fade(colors.right, 1 + wash * 0.5, 1 - wash));
  pc.poly([iso(u0, v0, h1), iso(u1, v0, h1), iso(u1, v1, h1), iso(u0, v1, h1)], between(shade(colors.top, 1.05), shade(colors.top, 0.95), 55));
  // Light catches the near edges: the top rim and the vertical corner.
  const rim = shade(colors.top, 1.16);
  pc.lineF(iso(u0, v1, h1).x, iso(u0, v1, h1).y, iso(u1, v1, h1).x, iso(u1, v1, h1).y, rim, 0.55);
  pc.lineF(iso(u1, v1, h1).x, iso(u1, v1, h1).y, iso(u1, v0, h1).x, iso(u1, v0, h1).y, shade(colors.top, 1.08), 0.5);
  if (tall > 3) {
    pc.lineF(iso(u1, v1, h0).x, iso(u1, v1, h0).y, iso(u1, v1, h1).x, iso(u1, v1, h1).y, shade(colors.left, 1.12), 0.5);
  }
}

/** Paint boxes back to front so nearer pieces overlap farther ones. */
export function drawBoxes(pc: PixelCanvas, pieces: BoxPiece[]): void {
  const depth = (b: BoxPiece) => (b.u0 + b.u1 + b.v0 + b.v1) / 2;
  const sorted = [...pieces].sort((a, b) => depth(a) - depth(b) || a.h0 - b.h0);
  for (const piece of sorted) drawBox(pc, piece);
}

export function box(
  u0: number,
  u1: number,
  v0: number,
  v1: number,
  h0: number,
  h1: number,
  colors: Faces,
): BoxPiece {
  return { u0, u1, v0, v1, h0, h1, colors };
}

/** Upright cylinder of radius r (tile units) centred on the tile. */
export function drawCylinder(
  pc: PixelCanvas,
  r: number,
  h0: number,
  h1: number,
  top: string,
  side: string,
  cu = 0,
  cv = 0,
): void {
  const rx = r * HW * Math.SQRT2;
  const ry = r * HH * Math.SQRT2;
  const c = iso(cu, cv);
  const body = {
    type: 'linear',
    x0: c.x - rx,
    y0: 0,
    x1: c.x + rx,
    y1: 0,
    stops: [[0, shade(side, 1.12)], [0.35, side], [0.75, shade(side, 0.82)], [1, shade(side, 0.7)]],
  } as const;
  pc.ellipse(c.x, c.y - h0, rx, ry, body);
  pc.rectF(c.x - rx, c.y - h1, rx * 2, h1 - h0, body);
  pc.ellipse(c.x, c.y - h1, rx, ry, between(shade(top, 1.06), shade(top, 0.94), 55));
}

export type Face = 'left' | 'right';

/**
 * Quad drawn onto one of the two visible vertical faces of a box. `s0..s1`
 * runs along the face in tile units; `at` is where the face plane sits.
 */
export function faceQuad(
  pc: PixelCanvas,
  face: Face,
  at: number,
  s0: number,
  s1: number,
  h0: number,
  h1: number,
  color: string,
): void {
  const p = (s: number, h: number) => (face === 'left' ? iso(s, at, h) : iso(at, s, h));
  pc.poly([p(s0, h0), p(s1, h0), p(s1, h1), p(s0, h1)], color);
}

/** The visible face a rotation points at, or null when it faces away from the viewer. */
export function frontFace(rotation: number): Face | null {
  if (rotation === 2) return 'right';
  if (rotation === 4) return 'left';
  return null;
}

/** Rotates a tile-space point by a multiple of 90 degrees (rotation 0/2/4/6 -> 0/1/2/3 turns). */
export function turn(u: number, v: number, rotation: number): [number, number] {
  const n = (((rotation ?? 0) / 2) % 4 + 4) % 4;
  switch (n) {
    case 1:
      return [-v, u];
    case 2:
      return [-u, -v];
    case 3:
      return [v, -u];
    default:
      return [u, v];
  }
}

/** Rotates an axis-aligned box's (u, v) extents by a multiple of 90 degrees. */
export function turnBox(
  u0: number,
  u1: number,
  v0: number,
  v1: number,
  rotation: number,
): [number, number, number, number] {
  const corners = [turn(u0, v0, rotation), turn(u1, v0, rotation), turn(u0, v1, rotation), turn(u1, v1, rotation)];
  const us = corners.map((c) => c[0]);
  const vs = corners.map((c) => c[1]);
  return [Math.min(...us), Math.max(...us), Math.min(...vs), Math.max(...vs)];
}

/** Pieces for a seat with a backrest on the side opposite its facing. */
export function seatWithBack(
  rotation: number,
  half: number,
  seatH: [number, number],
  backH: number,
  thickness: number,
  seat: Faces,
  back: Faces,
): BoxPiece[] {
  const [h0, h1] = seatH;
  const pieces = [box(-half, half, -half, half, h0, h1, seat)];
  const t = half - thickness;
  switch (rotation) {
    case 2: // faces +u, back along -u
      pieces.push(box(-half, -t, -half, half, h1, backH, back));
      break;
    case 4: // faces +v, back along -v
      pieces.push(box(-half, half, -half, -t, h1, backH, back));
      break;
    case 6: // faces -u, back along +u
      pieces.push(box(t, half, -half, half, h1, backH, back));
      break;
    default: // 0: faces -v, back along +v
      pieces.push(box(-half, half, t, half, h1, backH, back));
  }
  return pieces;
}

